import { useState, useEffect, useCallback, type ReactNode } from 'react'

import type { TokenContextType, UserTokenStatus } from '../types/token.types'
import { TokenContext } from '../contexts/TokenContext'
import { TokenService } from '../services/tokenService'

import { useAuth } from './useAuth'

export interface TokenProviderProps {
  children: ReactNode
}

export const TokenProvider = ({ children }: TokenProviderProps) => {
  const { user, loading: authLoading } = useAuth()

  const [tokenStatus, setTokenStatus] = useState<UserTokenStatus | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Calculate if user is rate limited
  const isRateLimited = tokenStatus?.currentTokens === 0

  // Calculate time until refill
  const timeUntilRefill = tokenStatus
    ? TokenService.formatTimeUntilRefill(tokenStatus.refillsAt)
    : null

  // Calculate token warning state
  const warningState = tokenStatus
    ? TokenService.getTokenWarningState(
        tokenStatus.currentTokens,
        tokenStatus.maxTokens
      )
    : { isLow: false, isCritical: false, isEmpty: false }

  // Refresh token status
  const refreshTokenStatus = useCallback(async () => {
    if (!user || authLoading) {
      return
    }

    setLoading(true)
    setError(null)

    try {
      const status = await TokenService.getUserTokenStatus()
      setTokenStatus(status)
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch token status'
      setError(errorMessage)
      console.error('Error fetching token status:', err)
    } finally {
      setLoading(false)
    }
  }, [user, authLoading])

  // Load token status when user changes
  useEffect(() => {
    if (user && !authLoading) {
      refreshTokenStatus()
    } else if (!user) {
      // Clear token status when user signs out
      setTokenStatus(null)
      setError(null)
      setLoading(false)
    }
  }, [user, authLoading, refreshTokenStatus])

  // Auto-refresh token status periodically (every 30 seconds)
  useEffect(() => {
    if (!user || !tokenStatus) return

    const interval = setInterval(() => {
      // Only refresh if we're not already loading
      if (!loading) {
        refreshTokenStatus()
      }
    }, 30000) // 30 seconds

    return () => clearInterval(interval)
  }, [user, tokenStatus, loading, refreshTokenStatus])

  // Auto-refresh at midnight UTC (when tokens refill)
  useEffect(() => {
    if (!tokenStatus) return

    const now = new Date()
    const nextRefill = TokenService.getNextRefillTime()
    const msUntilRefill = nextRefill.getTime() - now.getTime()

    // Only set timeout if refill is within 24 hours
    if (msUntilRefill > 0 && msUntilRefill <= 24 * 60 * 60 * 1000) {
      const timeout = setTimeout(() => {
        refreshTokenStatus()
      }, msUntilRefill + 1000) // Add 1 second buffer

      return () => clearTimeout(timeout)
    }
  }, [tokenStatus, refreshTokenStatus])

  const contextValue: TokenContextType = {
    tokenStatus,
    loading,
    error,
    refreshTokenStatus,
    isRateLimited,
    timeUntilRefill,
    warningState,
  }

  return (
    <TokenContext.Provider value={contextValue}>
      {children}
    </TokenContext.Provider>
  )
}
